import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, PartialObserver } from 'rxjs';

import { userServiceFields } from '../models/user-service-info';
import { AUTH_SERVER_URL } from '../../config';
import { NotificationResponse, SubscriberService, SubscriptionFrequencies, Subscriptions, UserNotification }
	from '../models/user-notification';
import { LANGUAGE_OPTIONS } from '../../shared/models/list';
import { AddServiceResponse, UserServiceResponse } from '../models/user-service';
import { OrderBy } from '../../shared/utils/constant';
import { UserNotifications } from '../models/v2/user-notifications';

const AUTH_USER = `${ AUTH_SERVER_URL }/v1`;
const AUTH_URL = `${ AUTH_SERVER_URL }/v2`;
const GET_USER = `${ AUTH_SERVER_URL }/v2/users`;

@Injectable({ providedIn: 'root' })
export class UserCardService {
	private readonly firstNameSubject = new BehaviorSubject('');
	private readonly lastNameSubject = new BehaviorSubject('');
	constructor(private readonly http: HttpClient) {}

	setFirstName(firstName: string) {
		this.firstNameSubject.next(firstName);
	}

	subscribeToFirstName(observer: PartialObserver<string | undefined>) {
		return this.firstNameSubject.subscribe(observer);
	}

	subscribeToLastName(observer: PartialObserver<string | undefined>) {
		return this.lastNameSubject.subscribe(observer);
	}

	setLastName(lastName: string) {
		this.lastNameSubject.next(lastName);
	}

	findUserNotification(lang: keyof typeof LANGUAGE_OPTIONS | undefined) {
		const url = new URL(`${ AUTH_USER }/users:current/notifications`);
		const params = url.searchParams;
		params.append('lang', lang ?? 'en');
		return this.http.get<UserNotification>(url.toString());
	}

	getUserNotifications(userId: string) {
		const url = new URL(`${ AUTH_URL }/users/${ userId }/notifications`);
		return this.http.get<UserNotifications[]>(url.toString());
	}

	deleteUserNotification(userId: string, organizationId: number, subscriptionDetailId: number) {
		const url = new URL(
			`${ AUTH_URL }/users/${ userId }
			/organizations/${ organizationId }
			/subscriptionDetail/${ subscriptionDetailId }`
		);
		return this.http.delete(url.toString());
	}

	updateUserPassword(oldPwd: string, newPwd: string, confNewPwd: string) {
		const url = new URL(
			`${ AUTH_URL }/updatePassword`
		);
		const passwords = {
			oldPwd,
			newPwd,
			confNewPwd,
		};
		return this.http.post(url.toString(), passwords);
	}

	getServicesForUserByOrganization(
		userId: number,
		filter?: string,
		page?: number,
		limit?: number,
		orderBy?: OrderBy<keyof UserServiceResponse>[] | OrderBy<keyof UserServiceResponse>,
		onlyCount = false,
		onlyDistinct = false
	) {
		const url = new URL(`${ GET_USER }/${ userId }/services`);
		const fields = [...userServiceFields];
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		if (page !== undefined) {
			params.append('page', String(page));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (onlyCount) {
			params.append('onlyCount', String(onlyCount));
		}
		if (onlyDistinct) {
			params.append('distinct', String(onlyDistinct));
		}
		return this.http.get<UserServiceResponse[]>(url.toString());
	}

	addServicesToUser(userId: string, supplierId: number, organizationId: string, services: AddServiceResponse[]) {
		const url = new URL(`${ AUTH_URL }/users/${ userId }/suppliers/${ supplierId }
		/organizations/${ organizationId }`);
		return this.http.post(url.toString(), services);
	}

	deleteServicesFromUser(
		userId: string,
		supplierId: number,
		organizationId: string,
		deleteServiceResponse: string[]
	) {
		const url = new URL(`${ AUTH_URL }/users/${ userId }/suppliers/${ supplierId }
		/organizations/${ organizationId }`);
		const options = {
			headers: new HttpHeaders({
				/* eslint-disable @typescript-eslint/naming-convention */
				'Content-Type': 'application/json'
			}),
			body: deleteServiceResponse
		};
		return this.http.delete(url.toString(), options);
	}

	getServicesLinkedToSubscription(
		userId: string,
		organizationId: string
	) {
		const url = new URL(`${ GET_USER }/${ userId }/organizations/${ organizationId }
			/notificationsServices`);
		return this.http.get<SubscriberService[]>(url.toString());
	}

	getSubscriptionsOfService(
		userId: string,
		organizationId: string,
		serviceCode: string
	) {
		const url = new URL(`${ GET_USER }/${ userId }/organizations/${ organizationId }
			/services/${ serviceCode }/subscriptions`);
		return this.http.get<Subscriptions[]>(url.toString());
	}

	getFrequenciesOfSubscription(
		subscriptionsId: string
	) {
		const url = new URL(`${ AUTH_URL }/subscriptions/${ subscriptionsId }/frequencies`);
		return this.http.get<SubscriptionFrequencies>(url.toString());
	}

	addNotification(
		notification: NotificationResponse,
		userId: string
	) {
		const url = new URL(`${ GET_USER }/${ userId }/notification`);
		return this.http.post(url.toString(), notification);
	}

	updateUserRole(organizationId: number, userId: number, adminRole: string) {
		const url = new URL(
			`${ GET_USER }/${ userId }/organizations/${ organizationId }/role/${ adminRole }`
		);
		return this.http.put(url.toString(), null);
	}
}
